import { Text } from '@wyz/ui';
import * as React from 'react';
import { NumberField } from '~/common/components';
import { useI18n } from '~/common/context';
import { SUPPLIER_TYPE_ID_TRANSLATIONS } from '~/common/translators';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  IDistributionExtraBonusDraft,
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicyDetailRow,
  IDistributionPricingPolicySeason,
} from '~/purchase/types';
/* eslint-disable-next-line @wyz/module/no-relative-import -- siblings are imported directly: going
   through the ~/purchase/components barrel would make this module depend on itself. */
import {
  ExtraBonusAmount,
  PurchaseAmount,
  TotalAmount,
} from './extra-bonus-cells';

const { NO_VALUE, tintClass } = DistributionPricingPolicyHelpers;

type Props = {
  brand: IDistributionPricingPolicyBrand;
  row: IDistributionPricingPolicyDetailRow;
  rowIndex: number;
  seasons: IDistributionPricingPolicySeason[];
  canSeeExtraBonus: boolean;
  isEdited: boolean;
  draft: IDistributionExtraBonusDraft;
};

export function ExtraBonusSupplierRow({
  brand,
  row,
  rowIndex,
  seasons,
  canSeeExtraBonus,
  isEdited,
  draft,
}: Props) {
  const { t } = useI18n();
  const { bonusSign, cellKey } = DistributionPricingPolicyHelpers;
  const supplierTypeKey = SUPPLIER_TYPE_ID_TRANSLATIONS[row.supplierTypeId];

  return (
    <tr>
      <td className='extra-bonus-table__supplier'>
        {supplierTypeKey ? t(supplierTypeKey) : String(row.supplierTypeId)}
      </td>

      {canSeeExtraBonus && 0 === rowIndex && (
        <td className='extra-bonus-table__hq' rowSpan={brand.rows.length}>
          {isEdited ? (
            <NumberField
              name='hqBonus'
              size='sm'
              withKnobs={false}
              sign={bonusSign(draft.bonusType)}
              feedbackType='tooltip'
            />
          ) : (
            <Text color='primary' weight='semi-bold'>
              {brand.hqBonus ?? NO_VALUE}
            </Text>
          )}
        </td>
      )}

      {seasons.map((season) => {
        const cell = row.seasons[String(season.id)];
        const tint = `extra-bonus-table__tint${tintClass(season)}`;
        const key = cellKey(row.supplierTypeId, season.id);
        // While editing, the cell is read through the draft so the total follows every keystroke.
        const draftedCell =
          isEdited && cell
            ? { ...cell, extraBonus: draft.values?.[key] ?? null }
            : cell;

        return (
          <React.Fragment key={season.id}>
            <td className={tint}>
              {cell ? <PurchaseAmount cell={cell} season={season} /> : NO_VALUE}
            </td>
            {canSeeExtraBonus && (
              <>
                <td className={`${tint} extra-bonus-table__highlight`}>
                  {isEdited && cell ? (
                    <NumberField
                      name={`values.${key}`}
                      size='sm'
                      withKnobs={false}
                      sign={bonusSign(draft.bonusType)}
                      feedbackType='tooltip'
                    />
                  ) : cell ? (
                    <ExtraBonusAmount cell={cell} season={season} />
                  ) : (
                    NO_VALUE
                  )}
                </td>
                <td className={`${tint} extra-bonus-table__highlight`}>
                  {draftedCell ? (
                    <TotalAmount
                      cell={draftedCell}
                      season={season}
                      extraBonusType={
                        isEdited
                          ? (draft.bonusType ?? null)
                          : (cell?.extraBonusType ?? null)
                      }
                      hqBonus={
                        isEdited ? (draft.hqBonus ?? null) : brand.hqBonus
                      }
                      hqBonusType={
                        isEdited ? (draft.bonusType ?? null) : brand.hqBonusType
                      }
                    />
                  ) : (
                    NO_VALUE
                  )}
                </td>
              </>
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
}
